<template>
  <div>
    <div
      v-for="form in formData"
      :key="form.id"
      class="form__block"
      :style="{ ...form.formStyle }"
    >
      <div v-if="form.title" class="form__block__title">{{ form.title }}</div>
      <div class="form__block__content" :style="getColumnGap(form)">
        <div
          v-for="field in (form.formFields || []).filter((item) =>
            formatAttribute(item, 'visible', true)
          )"
          :key="field.id"
          class="form__block__content__item"
          :class="{
            'form__block__content__item--title': field.type === 'title',
            'form__block__content__item--no-padding': field.type === 'radio',
          }"
          :style="{
            ...form.fieldStyle,
            ...field.style,
            ...getFieldWidth(form, field),
          }"
        >
          <radio
            v-if="field.type === 'radio'"
            type="group"
            :label="field.label"
            :name="field.name"
            :radio-array="getRadioArray(field)"
            :disabled="formatAttribute(field, 'disabled', false)"
            :hover="false"
            :focus="false"
            :error-message="
              fieldsWithError[field.name]
                ? fieldsWithError[field.name].errorMessage
                : ''
            "
            :required="
              typeof field.required === 'function'
                ? field.required(formState)
                : field.required
            "
            @onClick="onChange($event, field)"
          />
          <div v-else-if="field.type === 'title'">{{ field.label }}</div>
          <text-input
            v-else-if="field.type === 'input'"
            :id="field.name"
            :name="field.name"
            :label="field.label"
            :placeholder="field.placeholder"
            :value="formState[field.name] || ''"
            :disabled="formatAttribute(field, 'disabled', false)"
            :error="fieldsWithError[field.name] ? true : false"
            :validation="field.validationName"
            :validation-object="field.validationObject"
            :type="field.inputType || 'text'"
            :error-message="
              fieldsWithError[field.name]
                ? fieldsWithError[field.name].errorMessage
                : ''
            "
            :required="
              typeof field.required === 'function'
                ? field.required(formState)
                : field.required
            "
            @onChange="onChange($event, field)"
          />
          <text-area
            v-else-if="field.type === 'textarea'"
            :id="field.id"
            :name="field.name"
            :label="field.label"
            :placeholder="field.placeholder"
            :value="formState[field.name] || ''"
            :error="fieldsWithError[field.name] ? true : false"
            :error-message="
              fieldsWithError[field.name]
                ? fieldsWithError[field.name].errorMessage
                : ''
            "
            :required="
              typeof field.required === 'function'
                ? field.required(formState)
                : field.required
            "
            :disabled="formatAttribute(field, 'disabled', false)"
            @onChange="onChange($event, field)"
          />
          <date-input
            v-else-if="field.type === 'date'"
            :name="field.name"
            :label="field.label"
            :placeholder="field.placeholder"
            :required="
              typeof field.required === 'function'
                ? field.required(formState)
                : field.required
            "
            :disabled="formatAttribute(field, 'disabled', false)"
            :date-format="field.format || 'DD-MMM-YYYY'"
            :clearable="field.clearable || true"
            :value="getDateValue(field)"
            :allow-range="field.allowRange"
            :error="fieldsWithError[field.name] ? true : false"
            :error-message="
              fieldsWithError[field.name]
                ? fieldsWithError[field.name].errorMessage
                : ''
            "
            :min-date="field.minDate ? field.minDate : null"
            @input="onChange($event, field)"
            @clear="handleClear(field)"
          />
          <form-select
            v-else-if="field.type === 'select'"
            :id="field.id"
            :label="field.label"
            :required="
              typeof field.required === 'function'
                ? field.required(formState)
                : field.required
            "
            :options="
              typeof field.options === 'function'
                ? field.options(formState)
                : field.options
            "
            :error="fieldsWithError[field.name] ? true : false"
            :error-message="
              fieldsWithError[field.name]
                ? fieldsWithError[field.name].errorMessage
                : ''
            "
            :value="getSelectValue(field)"
            :disabled="formatAttribute(field, 'disabled', false)"
            @input="onChange($event, field)"
          />
          <checkbox
            v-else-if="field.type === 'checkbox'"
            :id="field.id"
            :key="field.id"
            :label="field.label"
            :checked="formState[field.name] === true ? true : false"
            :disabled="formatAttribute(field, 'disabled', false)"
            class="form__block__content__item__checkbox"
            @onClick="onChange($event, field)"
          />
          <dropdown
            v-else-if="field.type === 'searchDropdown'"
            :id="field.id"
            :label="field.label"
            :value="getDropdownValue(field)"
            :placeholder="field.placeholder"
            :options="field.options"
            :filter-name="field.name"
            :close-on-select="true"
            :disabled="formatAttribute(field, 'disabled', false)"
            :required="
              typeof field.required === 'function'
                ? field.required(formState)
                : field.required
            "
            :multiple="field.multiple"
            :custom-component="field.customComponent"
            :filterable="field.filterable"
            :loading="field.loading"
            :dropdown-label="field.dropdownLabel"
            :error="fieldsWithError[field.name] ? true : false"
            :error-message="
              fieldsWithError[field.name]
                ? fieldsWithError[field.name].errorMessage
                : ''
            "
            :clearable="field.clearable"
            @onChange="onChange($event, field)"
            @onSearch="onSearch($event, field)"
            @onOpenDropdown="onOpenDropdown()"
          />
          <mds-switch
            v-else-if="field.type === 'switch'"
            :id="field.id"
            :type="field.switchType || 'standard'"
            :help-message-position="field.helpMessagePosition"
            :label="field.label"
            :error-message="
              fieldsWithError[field.name]
                ? fieldsWithError[field.name].errorMessage
                : ''
            "
            :help-message="field.helpMessage"
            :switch-array="field.switchArray"
            :checked="formState[field.name] === true ? true : false"
            :disabled="formatAttribute(field, 'disabled', false)"
            @onClick="onChange($event, field)"
          />
          <div v-else-if="field.type === 'textbox'">
            <div class="form__block__content__item__label">
              {{
                typeof field.label === "function"
                  ? field.label(formState)
                  : field.label
              }}
              <span
                v-if="
                  typeof field.required === 'function'
                    ? field.required(formState)
                    : field.required
                "
                class="required"
              ></span>
            </div>
            <text-editor
              :default-comments="formState[field.name]"
              :label="field.label"
              :error="fieldsWithError[field.name] ? true : false"
              :error-msg="
                fieldsWithError[field.name]
                  ? fieldsWithError[field.name].errorMessage
                  : ''
              "
              :max-length="field.maxLength"
              @commentsChange="onChange($event, field)"
            ></text-editor>
          </div>
          <div v-else-if="field.type === 'text'">
            <div class="form__block__content__item__label">
              {{ field.label }}
            </div>
            <div class="form__block__content__item__value">
              {{ formState[field.name] }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="actions.length" class="form__action">
      <div v-for="button in actions" :key="button.appearance">
        <Button
          :appearance="button.appearance"
          :label="button.label"
          @onClick="validate(button.label)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TextInput from "../TextInput/TextInput.vue";
import Checkbox from "../Checkbox/Checkbox.vue";
import TextEditor from "../TextEditor/TextEditor.vue";
import TextArea from "../TextArea/TextArea.vue";
import Dropdown from "../Dropdown/Dropdown.vue";
import FormSelect from "../mds/FormSelect.vue";
import Switch from "../Switch/Switch.vue";
import DateInput from "../DateInput/DateInput.vue";
import Radio from "../Radio/Radio.vue";
import Button from "../Button/Button.vue";

import { formatToISOString, getZonedTime } from "../../utils/helpers";

export default {
  name: "Form",
  components: {
    TextInput,
    Radio,
    TextEditor,
    FormSelect,
    DateInput,
    Checkbox,
    TextArea,
    Dropdown,
    Button,
    "mds-switch": Switch,
  },
  props: {
    formData: {
      type: Array,
      default: () => [],
    },
    defaultValues: {
      type: Object,
      default: () => {},
    },
    actions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      formState: {},
      fieldsWithError: {},
      oldState: this.defaultValues,
      changedFields: {},
    };
  },
  watch: {
    formState: {
      deep: true,
      handler() {
        if (typeof this.onChange === "function") {
          const currentDiff = this.getObjectDifference(
            this.formState,
            this.oldState
          );
          const diffWithDefaultValues = this.getObjectDifference(
            this.formState,
            this.defaultValues
          );
          this.changedFields = { ...diffWithDefaultValues };
          this.$emit(
            "onChange",
            currentDiff,
            diffWithDefaultValues,
            this.formState
          );
          this.oldState = { ...this.formState };
        }
      },
    },
  },
  created() {
    this.getInitState();
  },
  methods: {
    formatAttribute(field, attrName, fallbackVal) {
      return typeof field[attrName] === "function"
        ? field[attrName](this.formState, this.defaultValues)
        : Object.prototype.hasOwnProperty.call(field, attrName)
        ? field[attrName]
        : fallbackVal;
    },
    getColumnGap(form) {
      return { "column-gap": `${form?.formStyle?.columnGap || "24px"}` };
    },
    getFieldWidth(form, field) {
      return {
        flex: `${field?.style?.flex || form?.fieldStyle?.flex || "0 48.5%"}`,
      };
    },
    getInitState() {
      const formFields = this.formData.map((form) => form.formFields);
      [].concat
        .apply([], formFields)
        .forEach((el) =>
          this.$set(
            this.formState,
            el.name,
            Object.prototype.hasOwnProperty.call(
              this.defaultValues || {},
              el.name
            )
              ? this.defaultValues[el.name]
              : typeof el?.defaultValue === "boolean"
              ? el?.defaultValue
              : el?.defaultValue || null
          )
        );
    },
    updateFormState(key, value) {
      this.formState[key] = value;
    },
    getRadioArray(field) {
      return (field.radioOptions || []).map((item) => ({
        ...item,
        checked: this.formState[field.name] === item.value,
      }));
    },
    getDateValue(field) {
      let date = this.formState[field.name] || null;
      if (date && !field.allowRange) {
        date =
          date[date.length - 1] === "Z"
            ? date
            : date.indexOf("T") > -1
            ? `${date.split(".")[0]}Z`
            : getZonedTime(date);
      }
      return field.allowRange
        ? this.formState[field.name]
        : date
        ? new Date(date)
        : null;
    },
    getSelectValue(field) {
      if (typeof field.options === "function") {
        return this.formState[field.name]
          ? (field.options(this.formState) || []).filter(
              (item) => item.value === this.formState[field.name]
            )[0]
          : field.options[0];
      }
      return this.formState[field.name]
        ? (field.options || []).filter(
            (item) =>
              item.value ===
              (field.stateValue === "optionObject"
                ? this.formState[field.name].value
                : this.formState[field.name])
          )[0]
        : field.options[0];
    },
    getDropdownValue(field) {
      if (field.multiple) {
        return this.formState?.[field.name] ? this.formState?.[field.name] : [];
      }
      const fieldValue =
        this.formState[field.name]?.value &&
        (field.options || []).filter(
          (item) => item.value === this.formState[field.name].value
        );
      return this.formState[field.name]?.value
        ? fieldValue?.[0] || this.formState[field.name]?.value
        : "";
    },
    onChange(e, field) {
      switch (field.type) {
        case "radio":
          this.formState[field.name] = e?.radioArray.find(
            (item) => item.checked
          ).value;
          break;
        case "date":
          if (field.allowRange) {
            this.formState[field.name] = e;
            break;
          }
          this.formState[field.name] = formatToISOString(e);
          break;
        case "textbox":
          this.formState[field.name] = e.html;
          break;
        case "select":
          if (field.stateValue === "optionObject") {
            this.formState[field.name] = e;
            break;
          }
          this.formState[field.name] = e.value;
          break;
        case "checkbox":
          this.formState[field.name] = e;
          break;
        case "searchDropdown":
          this.formState[field.name] = e.value || {};
          break;
        case "textarea":
          this.formState[field.name] = e;
          break;
        case "switch":
          this.formState[field.name] = e;
          break;
        default:
          this.formState[field.name] = e.target.value;
          break;
      }
    },
    handleClear(field) {
      this.formState[field.name] = null;
    },
    onSearch(e, field) {
      if (e.searchValue) {
        this.$emit("updateOnSearch", e, field);
      }
    },
    validate(label) {
      this.fieldsWithError = {};
      const formFields = this.formData.map((form) => form.formFields);
      const { hasError, errorMap } = this.validateForm(
        [].concat.apply([], formFields),
        this.formState
      );
      errorMap.forEach((value, key) =>
        this.$set(this.fieldsWithError, key, value)
      );
      if (!hasError) {
        const isNotVisible = [];
        [].concat.apply([], formFields).forEach((field) => {
          if (
            "visible" in field &&
            !field.visible(this.formState, this.defaultValues)
          ) {
            isNotVisible.push(field.name);
          }
        });
        isNotVisible.forEach((item) => {
          delete this.formState[item];
          delete this.changedFields[item];
        });
        this.$emit("onSubmitForm", this.formState, this.changedFields, label);
      } else {
        this.$emit("hasError", this.fieldsWithError);
      }
    },
    getFormState() {
      return this.formState;
    },
    getObjectDifference(latest = {}, old = {}) {
      const returnData = {};
      Object.keys(latest).forEach((key) => {
        if (
          !Object.prototype.hasOwnProperty.call(old, key) ||
          latest[key] !== old[key]
        ) {
          if (
            old[key] &&
            typeof old[key] === "object" &&
            Object.keys(old[key]).length > 0
          ) {
            const diff = this.getObjectDifference(latest[key], old[key]);
            const oldDiff = this.getObjectDifference(old[key], latest[key]);
            // handle in case object property get deleted
            if (
              Object.keys(diff).length === 0 &&
              Object.keys(oldDiff).length === 0
            ) {
              return;
            }
          }
          returnData[key] = latest[key];
        }
      });
      return returnData;
    },
    validateForm(formFields, formState) {
      let hasError = false;
      const errorMap = new Map();
      (formFields || [])
        .filter((item) => item?.validation)
        .forEach((field) => {
          let value = formState[field.name];
          if (field.type === "searchDropdown" && !field?.multiple) {
            value = formState[field.name]?.value;
          }
          if (field.type === "select" && field.stateValue === "optionObject") {
            value = formState[field.name]?.value;
          }
          const {
            rules,
            customValidation,
            minLength,
            minValue,
            maxValue,
            validateValue,
            errorMessage,
          } = field?.validation;

          if (typeof rules !== "undefined") {
            if (rules.includes("required")) {
              if (
                value === undefined ||
                value === null ||
                (typeof value === "string" && value.length === 0) ||
                (typeof value === "object" && !Object.keys(value).length)
              ) {
                hasError = true;
                errorMap.set(field.name, {
                  errorMessage: errorMessage || "This field is required",
                });
              }
            }
            if (rules.includes("email")) {
              if (value && !this.validateEmail(value)) {
                hasError = true;
                errorMap.set(field.name, {
                  errorMessage: "This email is invalid",
                });
              }
            }
          }

          if (typeof customValidation === "function") {
            const result = customValidation(value, field, formState);
            if (typeof result === "boolean" && result) {
              hasError = result;
              errorMap.set(field.name, {
                errorMessage: "This field has error",
              });
            } else if (
              typeof result === "object" &&
              typeof result.isError === "boolean"
            ) {
              const { isError, errorMessage } = result;
              if (isError) {
                hasError = isError;
                errorMap.set(field.name, { errorMessage });
              }
            } else {
              console.warn(
                "Skipping Form CustomValidation - Invalid Return Data."
              );
            }
          }

          if (minLength !== undefined) {
            const isLengthError = value.toString().trim().length < minLength;
            if (isLengthError) {
              hasError = true;
              errorMap.set(field.name, {
                errorMessage:
                  errorMessage || `Min length should be ${minLength}`,
              });
            }
          }

          // minimum value check
          if (!isNaN(minValue)) {
            if (value < minValue) {
              hasError = true;
              errorMap.set(field.name, {
                errorMessage: errorMessage || `Min value should be ${minValue}`,
              });
            }
          }

          // maximum value check
          if (!isNaN(maxValue)) {
            if (value > maxValue) {
              hasError = true;
              errorMap.set(field.name, {
                errorMessage: errorMessage || `Max value should be ${maxValue}`,
              });
            }
          }

          if (typeof validateValue !== "undefined" && validateValue !== value) {
            hasError = true;
            errorMap.set(field.name, {
              errorMessage:
                errorMessage || `This field should be ${validateValue}`,
            });
          }
        });
      return { hasError, errorMap };
    },
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    onOpenDropdown() {
      this.$emit("onOpenDropdown");
    },
  },
};
</script>

<style lang="scss" scoped>
.form__block {
  margin-bottom: 24px;
  border: 1px solid var(--Gray-300);
  border-radius: 5px;
  padding: 24px 24px;
  &__title {
    font-weight: 700;
    font-size: 16px;
    margin-bottom: 16px;
  }
  &__content {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    &__item {
      padding-bottom: 24px;
      $self: &;
      &--title {
        padding: 24px 0;
        font-weight: 700;
        font-size: 16px;
      }
      &--no-padding {
        padding-bottom: 0;
      }
      #{ $self }__label {
        margin-bottom: 8px;
        .required:after {
          content: " *";
          color: var(--Danger);
        }
      }
      #{ $self }__checkbox {
        margin-top: 28px;
      }
    }
  }
}
</style>
<style lang="scss">
.form__block {
  .mx-datepicker {
    width: 100%;
  }
}
</style>
